import { FunctionComponent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, DatesRangeValue, DateValue } from "@mantine/dates";
import { faChevronLeft, faChevronRight, faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import dayjs from "dayjs";
import { kebabCase } from "lodash";
import { ActionIcon } from "../../action-icon";
import { Box } from "../../box";
import { Button } from "../../button";
import { Group } from "../../group";
import { Icon } from "../../icon";
import { Input } from "../../input";
import { Menu } from "../../menu";
import { ScrollArea } from "../../scroll-area";
import { Stack } from "../../stack";
import { Text } from "../../text";
import { TimePicker } from "../../time-picker";
import { Tooltip } from "../../tooltip";
import { MISSING_END_DATE_TOOLTIP, RelativeDates, TIME_RANGE_TOOLTIP } from "../date-picker-input.constants";
import {
    addCustomDayProps,
    formatDate,
    getDateTimeValue,
    getMaxDate,
    getMinDate,
    getRelativeDatesInfo,
} from "../date-picker-input.utilities";
import styles from "./date-picker-panel.module.scss";
import { getInitialPanelRange } from "./date-picker-panel.utilities";
import { IProps } from "./date-picker-panel.types";

/**
 * The body of the date range popover, without the popover or a form: relative presets, a two-month range calendar,
 * optional start and end times, and Cancel/Done. `DatePickerInput` renders it in its own popover and writes the result
 * to Formik; anything else can mount it inline (the Ask variable picker) and take the result from `onApply`.
 * - It opens on `startDate`/`endDate`, or on `relativeDateTime`'s range with that preset selected, or on the last 14
 *   days, and it resets to them whenever they change.
 * - Picking a preset selects its range and times. Picking days or changing a time deselects it, so Done applies a
 *   preset only while one is still selected, and the absolute range otherwise.
 * - Done is disabled until the range has an end date, its times are in order and it sits inside any restriction, with
 *   a tooltip saying why.
 * @param props See `IProps`.
 * @returns The panel.
 */
const DatePickerPanel: FunctionComponent<IProps> = ({
    startDate,
    endDate,
    relativeDateTime,
    limitSelectionMonths,
    limitPastDays,
    showRelativeDateValues,
    allowTimeSelect,
    minDate,
    maxDate = new Date(),
    infoLabel,
    omittedRelativeDates,
    autoFocusPreset = false,
    onApply,
    onCancel,
}) => {
    const [initialRange] = useState(() => getInitialPanelRange(startDate, endDate, relativeDateTime));
    const [value, setValue] = useState<DatesRangeValue<DateValue>>(initialRange.value);
    const [selectedRelativeDateTime, setSelectedRelativeDateTime] = useState<RelativeDates | null>(
        relativeDateTime ?? null
    );
    const [startTime, setStartTime] = useState<string | undefined>(initialRange.startTime);
    const [endTime, setEndTime] = useState<string | undefined>(initialRange.endTime);
    const [calendarDate, setCalendarDate] = useState<Date>(initialRange.calendarDate);

    /**
     * Select a preset: its range, its start month and its times.
     * @param label The preset.
     */
    const onPresetSelect = useCallback((label: RelativeDates) => {
        const presetRange = getRelativeDatesInfo()[label];
        const range = getInitialPanelRange(presetRange.startDate, presetRange.endDate, label);
        setValue(range.value);
        setSelectedRelativeDateTime(label);
        setCalendarDate(range.calendarDate);
        setStartTime(range.startTime);
        setEndTime(range.endTime);
    }, []);

    // Reset to the given range or preset whenever it changes, as the popover always has.
    useEffect(() => {
        const range = getInitialPanelRange(startDate, endDate, relativeDateTime);
        setSelectedRelativeDateTime(relativeDateTime ?? null);
        setValue(range.value);
        setCalendarDate(range.calendarDate);
        setStartTime(range.startTime);
        setEndTime(range.endTime);
    }, [endDate, relativeDateTime, startDate]);

    /**
     * Take the calendar's new range, which deselects any preset. Clearing both ends restarts the range from today.
     * @param newValue The calendar's range.
     */
    const handleDateChange = useCallback((newValue: DatesRangeValue<DateValue>) => {
        setSelectedRelativeDateTime(null);
        if (newValue[0] === null && newValue[1] === null) {
            setValue([new Date(), null]);
        } else {
            setValue(newValue);
        }
    }, []);

    const relativeDatesToShow = useMemo(() => {
        return (Object.keys(getRelativeDatesInfo()) as RelativeDates[]).filter(
            (label) => !omittedRelativeDates?.includes(label)
        );
    }, [omittedRelativeDates]);

    // One resolution of the presets per render, shared by the preset list below. Deliberately not
    // memoized and deliberately not hoisted out of the component: getRelativeDatesInfo() resolves
    // against "now", so a single call per render keeps every preset in the list consistent with each
    // other while still tracking the clock across a long-lived render.
    const relativeDatesInfo = getRelativeDatesInfo();

    // The absolute earliest selectable date, computed fresh on every render (mirroring the calendar's
    // own inline getMinDate call below) so presets and calendar days cannot drift apart as real time
    // advances between renders without a prop change. rangeStart is null and limitSelectionMonths is
    // omitted on purpose: we want the absolute floor, not the floor relative to an in-progress selection.
    const presetFloor = getMinDate({
        minDate: minDate ? dayjs(minDate) : undefined,
        limitPastDays,
        rangeStart: null,
    });

    // The preset a surrounding focus trap starts on: the selected one, or the first.
    const autoFocusLabel = useMemo(
        () => (autoFocusPreset ? (selectedRelativeDateTime ?? relativeDatesToShow[0]) : undefined),
        [autoFocusPreset, relativeDatesToShow, selectedRelativeDateTime]
    );

    /**
     * Take a new start time, which deselects any preset.
     * @param value The time, as `HH:mm`.
     */
    const handleStartTimeChange = useCallback((value: string) => {
        setSelectedRelativeDateTime(null);
        setStartTime(value);
    }, []);

    /**
     * Take a new end time, which deselects any preset.
     * @param value The time, as `HH:mm`.
     */
    const handleEndTimeChange = useCallback((value: string) => {
        setSelectedRelativeDateTime(null);
        setEndTime(value);
    }, []);

    /** Time range is not valid when:
     * - The start time is after the end time.
     * - The start time is the same as the end time and the start date is the same as the end date.
     */
    const isTimeRangeValid = useMemo(() => {
        if (!allowTimeSelect || !value[0] || !value[1]) return true;
        const start = dayjs(value[0]);
        const end = dayjs(value[1]);
        if (start.isAfter(end, "day")) return false;
        if (start.isSame(end, "day") && startTime === endTime) return false;
        return true;
    }, [allowTimeSelect, value, startTime, endTime]);

    const isWithinDateRestriction = useMemo(() => {
        const startDateTime = getDateTimeValue(value[0], startTime);
        const endDateTime = getDateTimeValue(value[1], endTime, true);

        // Compare at day granularity so end-of-day times (e.g. 23:59) on the
        // current day remain valid even though they are technically "in the
        // future" relative to the current moment when maxDate defaults to now.
        if (startDateTime && minDate && dayjs(startDateTime).isBefore(minDate, "day")) {
            return false;
        }
        if (endDateTime && maxDate && dayjs(endDateTime).isAfter(maxDate, "day")) {
            return false;
        }

        return true;
    }, [endTime, maxDate, minDate, startTime, value]);

    // Whether what the user has actually entered is usable: the times are in order, and the range
    // sits inside any min/max restriction. This deliberately excludes the missing end date, because
    // it drives the red highlight on the time inputs and a half-finished selection is not something
    // the user has got *wrong* -- so neither input should be flagged for it.
    const isDateRangeValid = isTimeRangeValid && isWithinDateRestriction;

    // A range with a start but no end is only ever an in-progress selection: the initial range and
    // `onPresetSelect` always set both, and `handleDateChange` guarantees value[0] is non-null. Done
    // used to be enabled in this state but committed nothing, silently discarding the start date the
    // user had just picked (GAS-716).
    const isEndDateSelected = value[1] !== null;

    const canApplyDateRange = isEndDateSelected && isDateRangeValid;

    /** Apply the selected preset, or else the absolute range with its times. */
    const handleDone = useCallback(() => {
        if (selectedRelativeDateTime) {
            onApply({ relativeDateTime: selectedRelativeDateTime });
            return;
        }
        const startDateTime = getDateTimeValue(value[0], startTime);
        const endDateTime = getDateTimeValue(value[1], endTime, true);
        if (startDateTime && endDateTime) {
            onApply({ startDateTime, endDateTime });
        }
    }, [endTime, onApply, selectedRelativeDateTime, startTime, value]);

    return (
        <Group className={styles["relative-dates-container"]} gap="md" bdrs="lg">
            {showRelativeDateValues && (
                <Stack className={styles["relative-dates"]} p="sm" gap="0" miw={100}>
                    <Menu>
                        {relativeDatesToShow.map((label) => {
                            const preset = relativeDatesInfo[label];

                            return (
                                <Menu.Item
                                    key={label}
                                    onClick={() => onPresetSelect(label)}
                                    active={selectedRelativeDateTime === label}
                                    disabled={
                                        presetFloor ? dayjs(preset.startDate).isBefore(presetFloor, "day") : false
                                    }
                                    data-testid={`date-options_${kebabCase(label)}`}
                                    {...(label === autoFocusLabel && { "data-autofocus": true })}
                                >
                                    {preset.formatedName}
                                </Menu.Item>
                            );
                        })}
                    </Menu>
                </Stack>
            )}

            <Stack className={styles["date-picker-container"]} p="lg" bdrs="lg">
                <ScrollArea className={styles["calendar-scroll-area"]}>
                    {typeof limitSelectionMonths !== "undefined" && limitSelectionMonths > 0 && (
                        <Group gap="sm">
                            <Icon className={styles["info-icon"]} icon={faCircleInfo} size="xxs" />
                            <Text className={styles["info-text"]} size="xs">
                                {infoLabel ??
                                    `Maximum range is ${limitSelectionMonths} month${limitSelectionMonths === 1 ? "" : "s"}.`}
                            </Text>
                        </Group>
                    )}
                    <DatePicker
                        onChange={handleDateChange}
                        value={value}
                        type="range"
                        minDate={getMinDate({
                            minDate: minDate ? dayjs(minDate) : undefined,
                            limitPastDays,
                            limitSelectionMonths,
                            rangeStart: value[0] !== null && value[1] === null ? dayjs(value[0]) : null,
                        })}
                        maxDate={getMaxDate({
                            maxDate: dayjs(maxDate),
                            limitSelectionMonths,
                            rangeStart: value[1] === null ? (value[0] !== null ? dayjs(value[0]) : null) : null,
                        })}
                        date={calendarDate}
                        onDateChange={setCalendarDate}
                        previousIcon={
                            <ActionIcon variant="default" data-testid="previous-month-button">
                                <Icon icon={faChevronLeft} size="xxs" />
                            </ActionIcon>
                        }
                        previousLabel="Previous Month"
                        nextIcon={
                            <ActionIcon variant="default" data-testid="next-month-button">
                                <Icon icon={faChevronRight} size="xxs" />
                            </ActionIcon>
                        }
                        nextLabel="Next Month"
                        numberOfColumns={2}
                        getDayAriaLabel={(date) => `date-picker_${kebabCase(formatDate(date))}`}
                        getDayProps={(date) => addCustomDayProps(date)}
                        allowSingleDateInRange
                        classNames={{
                            day: styles["day"],
                            monthRow: styles["monthRow"],
                            monthCell: styles["monthCell"],
                        }}
                    />
                </ScrollArea>
                <Group gap="sm" mt="auto" align="flex-end" justify="space-between">
                    {allowTimeSelect && (
                        <Group gap="sm" w={225}>
                            <Input.Wrapper variant="filled" flex={1} label={formatDate(value[0])}>
                                <TimePicker
                                    value={startTime}
                                    onChange={handleStartTimeChange}
                                    variant="filled"
                                    data-testid="date-picker-input-start-time"
                                    error={!isDateRangeValid ? true : undefined}
                                />
                            </Input.Wrapper>

                            <Input.Wrapper
                                variant="filled"
                                flex={1}
                                label={value[1] ? formatDate(value[1]) : "No date selected"}
                            >
                                <TimePicker
                                    // Showing a concrete time beside a "No date selected"
                                    // label reads as a real value for a date that does not
                                    // exist. Blank the display only -- `endTime` itself is
                                    // kept so completing the range restores it rather than
                                    // silently committing midnight.
                                    value={isEndDateSelected ? endTime : ""}
                                    onChange={handleEndTimeChange}
                                    variant="filled"
                                    data-testid="date-picker-input-end-time"
                                    error={!isDateRangeValid ? true : undefined}
                                />
                            </Input.Wrapper>
                        </Group>
                    )}

                    <Group gap="sm" ml="auto">
                        <Button onClick={onCancel} variant="default" data-testid="cancel-button">
                            Cancel
                        </Button>
                        <Tooltip
                            label={
                                !isEndDateSelected
                                    ? MISSING_END_DATE_TOOLTIP
                                    : isTimeRangeValid
                                      ? "Select a date range within the allowed spotlight range."
                                      : TIME_RANGE_TOOLTIP
                            }
                            disabled={canApplyDateRange}
                        >
                            {/* Browsers do not dispatch mouse events from a natively
                                disabled button, so a Tooltip attached straight to it never
                                opens -- which is why the existing invalid-time tooltip has
                                never been visible either. Hang it off a wrapper that still
                                receives pointer events, as `select-menu` already does for
                                its disabled items. */}
                            <Box data-testid="done-button-wrapper">
                                <Button onClick={handleDone} disabled={!canApplyDateRange} data-testid="done-button">
                                    Done
                                </Button>
                            </Box>
                        </Tooltip>
                    </Group>
                </Group>
            </Stack>
        </Group>
    );
};

export default memo(DatePickerPanel);
